import { ReactElement, useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import FlexViewHeader from './FlexViewHeader';
import { Tag } from '../../db/db';
import { Page, PageFilter, PageSortOrder, fetchAllPages, fetchAllPagesForHumanTag } from '../../db/pages';
import { sortTags, pageToTagSortOrder } from '../sort';
import CollapsibleTag from './pagelists/CollapsibleTag';
import RawPageList from './pagelists/RawPageList';
import TagPageList from './pagelists/TagPageList';
import Inspector from '../inspector/Inspector';
import SplitPane from 'react-split-pane';
import { clearSelectionAndSelectMulti } from '../../store/uiSlice';
import { useLiveQuery } from 'dexie-react-hooks';
import { useAppDispatch } from '../../store/hooks';

interface FlexViewProps {
  singleTag?: Tag;
  tags?: Tag[];
  pages?: Page[];
  defaultGroupByWindow?: boolean;
  defaultSortBy?: PageSortOrder;
  defaultFilter?: PageFilter;
  overrideExpandedTags?: Tag[];
  sortOrderOverride?: PageSortOrder | null;
}

const FlexView = ({
  singleTag = undefined,
  tags = [],
  pages = [],
  defaultGroupByWindow = false,
  defaultSortBy = PageSortOrder.recent,
  defaultFilter = { flagged: false },
  overrideExpandedTags = [],
  sortOrderOverride = null,
}: FlexViewProps): ReactElement => {
  const [groupByWindow, setGroupByWindow] = useState(defaultGroupByWindow);
  const [sortOrder, setSortOrder] = useState(defaultSortBy);
  const [filter, setFilter] = useState(defaultFilter);

  // Callback ref so RawPageList can virtualise the flat list against this scroll container.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const scrollRef = useCallback((el: HTMLDivElement | null) => setScrollEl(el), []);

  // Measure the height of content above the RawPageList so the virtualizer can account
  // for the offset (tags section + hr). Without this, virtual items appear too low when
  // the user scrolls past the tag section in search results.
  const aboveListRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    const el = aboveListRef.current;
    if (!el) return;
    const measure = () => setScrollMargin(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const prevSortOverride = useRef(sortOrderOverride);
  useEffect(() => {
    if (sortOrderOverride != null && sortOrderOverride !== prevSortOverride.current) {
      setSortOrder(sortOrderOverride);
      if (sortOrderOverride === PageSortOrder.similarity) {
        setGroupByWindow(false);
      }
    }
    prevSortOverride.current = sortOrderOverride;
  }, [sortOrderOverride]);

  const shouldFetchAll = pages.length === 0 && tags.length === 0 && singleTag == null;
  const AllPages = useLiveQuery(
    () => (shouldFetchAll ? fetchAllPages() : undefined),
    [shouldFetchAll]
  );
  const dispatch = useAppDispatch();

  // Synchronize inspector size across tabs?  Argh rerenders...
  // const inspectorSize = useAppSelector(selectInspectorSize);

  useEffect(() => {
    // log('Main useEffect keyDownHandler');
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'a' && event.metaKey) {
        event.preventDefault();
        dispatch(clearSelectionAndSelectMulti({ urls: pages.map((page) => page.nurl) }));
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    // 👇️ clean up event listener
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [pages]);

  // Prepare Tags — deduplicate by name to guard against any duplicate entries
  // that could cause React's "two children with the same key" warning.
  const tagsSorted = sortTags(tags, pageToTagSortOrder(sortOrder));
  const seenTagNames = new Set<string>();
  const uniqueTagsSorted = tagsSorted.filter((tag) => {
    if (seenTagNames.has(tag.name)) return false;
    seenTagNames.add(tag.name);
    return true;
  });
  // Don't pass groupByWindow in to tags, because that'd be annoying?
  const tagList = uniqueTagsSorted.map((tag) => (
    <CollapsibleTag
      key={tag.name}
      tag={tag.name}
      sortOrder={sortOrder}
      filter={filter}
      groupByWindow={groupByWindow}
      overrideExpanded={overrideExpandedTags.includes(tag)}
    />
  ));

  const singleTagList = singleTag && (
    <TagPageList
      tag={singleTag?.name}
      sortOrder={sortOrder}
      filter={filter}
      groupByWindow={groupByWindow}
      expanded={true}
      scrollEl={scrollEl}
    />
  );
  var rawPages: Page[] = pages;

  if (pages.length === 0 && tags.length === 0 && singleTag == null) {
    rawPages = AllPages || [];
  }

  const rawPageList = pages && (
    <RawPageList
      pages={pages}
      sortOrder={sortOrder}
      filter={filter}
      groupByWindow={groupByWindow}
      scrollEl={scrollEl}
      scrollMargin={scrollMargin}
    />
  );

  return (
    /* this lib is incompatible with react18. To fix
     children: React.ReactNode; needs to be added to SplitPaneProps.
     @ts-ignore TS2322 */
    <SplitPane
      allowResize={true}
      split="vertical"
      defaultSize={300} // inspectorSize
      minSize={10}
      primary="second"
      // onDragFinished={(size) => dispatch(setInspectorSize(size))}
    >
      <div className="fill flexContainerHorizontal mainPane">
        <div
          className="flexFillHorizontal"
          ref={scrollRef}
          style={{
            overflow: 'scroll',
          }}
        >
          <div ref={aboveListRef}>
            {tagList.length > 0 && <div>{tagList}</div>}
            {singleTagList != null && <div>{singleTagList}</div>}
            {tagList.length > 0 && pages.length > 0 && <hr></hr>}
          </div>

          {rawPageList}
        </div>

        <div
          style={{
            borderLeft: '1px solid',
            borderColor: 'rgb(50, 56, 62)',
            height: '100%',
          }}
        ></div>
      </div>
      <Inspector onSelectAll={async () => {
        let urlsToSelect: string[] = [];
        if (pages.length > 0) {
          urlsToSelect = pages.map((page) => page.nurl.normalized);
        } else if (singleTag) {
          const tagPages = await fetchAllPagesForHumanTag(singleTag.name);
          urlsToSelect = tagPages.map((page) => page.nurl.normalized);
        } else if (tags.length > 0) {
          const allTagPages = await Promise.all(tags.map((t) => fetchAllPagesForHumanTag(t.name)));
          urlsToSelect = [...new Set(allTagPages.flat().map((page) => page.nurl.normalized))];
        } else {
          const all = AllPages || (await fetchAllPages());
          urlsToSelect = all.map((page) => page.nurl.normalized);
        }
        dispatch(
          clearSelectionAndSelectMulti({
            urls: urlsToSelect.map((u) => ({ normalized: u } as any)),
          })
        );
      }}>
        <FlexViewHeader
          tagsSorted={tagsSorted}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          filter={filter}
          setFilter={setFilter}
          groupByWindow={groupByWindow}
          setGroupByWindow={setGroupByWindow}
        />
      </Inspector>
    </SplitPane>
  );
};

export default FlexView;
// function dispatch(arg0: any) {
//   throw new Error('Function not implemented.');
// }
